/**
 * Application router.
 *
 * - `/login` renders bare (no shell).
 * - Every other route renders inside `AppShellLayout` so they all
 *   share the navbar + header.
 * - Module routes are placeholders until those phases land.
 */

import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AppShellLayout } from "./AppShellLayout";
import { Login } from "@/features/auth/Login";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { RequireRole } from "@/features/auth/RequireRole";
import { RolePreviewProvider } from "@/features/auth/RolePreviewProvider";
import { AuditViewer } from "@/features/admin/AuditViewer";
import { ProvisionUser } from "@/features/admin/ProvisionUser";
import { UserDetail } from "@/features/admin/UserDetail";
import { UsersList } from "@/features/admin/UsersList";
import { ArchiveContainerDetailPage } from "@/features/archive/ArchiveContainerDetailPage";
import { ArchiveContainersPage } from "@/features/archive/ArchiveContainersPage";
import { ArchiveDatasetsPage } from "@/features/archive/ArchiveDatasetsPage";
import { ArchiveSearchPage } from "@/features/archive/ArchiveSearchPage";
import { NewArchiveContainerPage } from "@/features/archive/NewArchiveContainerPage";
import { TicketDetail } from "@/features/admin/TicketDetail";
import { VeevaApproval } from "@/features/admin/VeevaApproval";
import { SecondLineSupport } from "@/features/solutions/validation/second-line-support";
import { TicketsList } from "@/features/admin/TicketsList";
import { BlobBrowser } from "@/features/integration/BlobBrowser";
import { CaraBrowser } from "@/features/integration/CaraBrowser";
import { ConnectionDetail } from "@/features/integration/ConnectionDetail";
import { IntegrationsHub } from "@/features/integration/IntegrationsHub";
import { NewConnection } from "@/features/integration/NewConnection";
import { S3Browser } from "@/features/integration/S3Browser";
import { ConnectionBrowserPage } from "@/features/browse/ConnectionBrowserPage";
import { CollectionDetail } from "@/features/collections/CollectionDetail";
import { CollectionsList } from "@/features/collections/CollectionsList";
import { NewCollection } from "@/features/collections/NewCollection";
import { DatabaseBrowser } from "@/features/integration/DatabaseBrowser";
import { VeevaBrowser } from "@/features/integration/VeevaBrowser";
import {
  SolutionPlaceholderPage,
  type SolutionPlaceholderSlug,
} from "@/features/modules/SolutionPlaceholders";
import { NotificationsPage } from "@/features/notifications/NotificationsPage";
import { CreatePicker } from "@/features/solutions/create/CreatePicker";
import { TranslatePage } from "@/features/solutions/create/translate/TranslatePage";
import { CreateQaPage } from "@/features/solutions/create/qa/CreateQaPage";
import { ExploreChat, ExploreList } from "@/features/solutions/explore";
import { PipelineCanvas } from "@/features/pipelines/PipelineCanvas";
import { NewPipeline } from "@/features/projects/NewPipeline";
import { NewProject } from "@/features/projects/NewProject";
import { ProjectDetail } from "@/features/projects/ProjectDetail";
import { ProjectsHub } from "@/features/projects/ProjectsHub";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { Me } from "@/features/users/Me";
import { QualityPage } from "@/features/solutions/quality";
import { AssignmentsPage } from "@/features/solutions/clinical/assignments/AssignmentsPage";
import { EtmfCompletenessPage } from "@/features/solutions/clinical/etmf-completeness/EtmfCompletenessPage";
import { EtmfIntakePage } from "@/features/solutions/clinical/EtmfIntakePage";
import { ReviewPage } from "@/features/solutions/review";
import { MyTests } from "@/features/validation/MyTests";
import { NewRun } from "@/features/validation/NewRun";
import { RunDetail } from "@/features/validation/RunDetail";
import { ValidationHub } from "@/features/validation/ValidationHub";

/** Redirects an old `/integration/<type>/<id>` browse URL to its new home under
 *  `/browse`. Browsing moved out of Integrations, but links of the old shape
 *  may still exist in bookmarks, notes and chat history. */
/** Solutions routes that show a placeholder until their page lands. Every one
 *  is open to all signed-in roles (ADR-0018); the paths are final, so the real
 *  page replaces the element without moving the URL. */
const SOLUTION_PLACEHOLDER_ROUTES: { path: string; slug: SolutionPlaceholderSlug }[] = [];

/** `/ai/chats/:chatId` → the same chat in Explore. Chats became Explore
 *  (todo-demo-08); old links in bookmarks and notes keep working. */
function LegacyChatRedirect() {
  const { chatId = "" } = useParams();
  return <Navigate to={`/solutions/explore/${chatId}`} replace />;
}

function LegacyBrowseRedirect({ sourceType }: { sourceType: string }) {
  const { connectionId = "" } = useParams();
  return <Navigate to={`/browse/${sourceType}/${connectionId}`} replace />;
}

export function AppRoutes() {
  return (
    // Above every RequireRole and AppShellLayout so the "view as role" preview
    // is shared by the route guards AND the nav, and survives navigation
    // (a per-layout provider would reset it on each page change).
    <RolePreviewProvider>
      <Routes>
        <Route path="/" element={<Navigate to="/me" replace />} />
        <Route path="/login" element={<Login />} />

        <Route
          path="/me"
          element={
            <RequireAuth>
              <AppShellLayout>
                <Me />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        <Route
          path="/settings"
          element={
            <RequireAuth>
              <AppShellLayout>
                <SettingsPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* The full list behind the header bell. Any signed-in user may open
            it — the notifications themselves are scoped by what the underlying
            read returns, so someone without pipelines simply sees none. */}
        <Route
          path="/notifications"
          element={
            <RequireAuth>
              <AppShellLayout>
                <NotificationsPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        <Route
          path="/admin/users"
          element={
            <RequireRole role="administrator">
              <AppShellLayout>
                <UsersList />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/admin/users/new"
          element={
            <RequireRole role="administrator">
              <AppShellLayout>
                <ProvisionUser />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <RequireRole role="administrator">
              <AppShellLayout>
                <TicketsList />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/admin/approvals/veeva/:changeId"
          element={
            <RequireRole role="administrator">
              <AppShellLayout>
                <VeevaApproval />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tickets/:ticketId"
          element={
            <RequireRole role="administrator">
              <AppShellLayout>
                <TicketDetail />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <RequireRole role="administrator">
              <AppShellLayout>
                <UserDetail />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <AuditViewer />
              </AppShellLayout>
            </RequireRole>
          }
        />

        {/* The hub's three views are tabs, but each keeps its own URL so it can
            be linked to and landed on. The guards are per-route and unchanged:
            Connected and Add connection are Developer+, Roles is any signed-in
            user — the hub hides the tabs a viewer's role would be refused. */}
        <Route
          path="/integration/connections"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <IntegrationsHub tab="connected" />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/integration/bindings"
          element={
            <RequireAuth>
              <AppShellLayout>
                <IntegrationsHub tab="roles" />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/integration/available"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <IntegrationsHub tab="available" />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/integration/connections/new"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <NewConnection />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/integration/connections/:connectionId"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <ConnectionDetail />
              </AppShellLayout>
            </RequireRole>
          }
        />
        {/* Browsing is its own destination (left nav), with the connection as a
            dropdown inside it. `:connectionId?` is optional so the nav link can
            land on the picker; selecting one fills it in. */}
        <Route
          path="/browse/veeva/:connectionId?"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <ConnectionBrowserPage
                  sourceType="veeva"
                  title="Veeva Vault"
                  description="Query a vault with VQL, or browse its File Staging (FTP) area."
                >
                  {(id) => <VeevaBrowser connectionId={id} />}
                </ConnectionBrowserPage>
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/browse/cara/:connectionId?"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <ConnectionBrowserPage
                  sourceType="cara"
                  title="Generis CARA"
                  description="Search a CARA tenant's records. Read-only — nothing here writes to CARA."
                >
                  {(id) => <CaraBrowser connectionId={id} />}
                </ConnectionBrowserPage>
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/browse/blob/:connectionId?"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <ConnectionBrowserPage
                  sourceType="blob"
                  title="Azure Blob Storage"
                  description="Browse containers and files, upload, and download."
                >
                  {(id) => <BlobBrowser connectionId={id} />}
                </ConnectionBrowserPage>
              </AppShellLayout>
            </RequireRole>
          }
        />

        <Route
          path="/browse/database/:connectionId?"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <ConnectionBrowserPage
                  sourceType="database"
                  title="Database"
                  description="Browse a connected database's schemas and tables, and preview rows. Read-only."
                >
                  {(id) => <DatabaseBrowser connectionId={id} />}
                </ConnectionBrowserPage>
              </AppShellLayout>
            </RequireRole>
          }
        />

        <Route
          path="/browse/s3/:connectionId?"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <ConnectionBrowserPage
                  sourceType="s3"
                  title="AWS S3"
                  description="Browse buckets and objects, upload, and download."
                >
                  {(id) => <S3Browser connectionId={id} />}
                </ConnectionBrowserPage>
              </AppShellLayout>
            </RequireRole>
          }
        />

        {/* The old per-connection browse URLs. Kept as redirects so existing
            bookmarks and any link still pointing at them land in the right
            place rather than on a blank route. */}
        <Route
          path="/integration/veeva/:connectionId"
          element={<LegacyBrowseRedirect sourceType="veeva" />}
        />
        <Route
          path="/integration/blob/:connectionId"
          element={<LegacyBrowseRedirect sourceType="blob" />}
        />

        {/* Solutions → Archive. Search first: for an end user the archive is
            something you search, not an inventory you browse; the container
            list is the records manager's view. Every role may enter (the
            `User` role holds archive.search / read / retrieve) — which archive
            a person can actually open is decided per container by a grant,
            server-side. Creating a container is an Administrator act. */}
        <Route
          path="/archive"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ArchiveSearchPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/archive/search"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ArchiveSearchPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/archive/containers"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ArchiveContainersPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        {/* Before `/archive/containers/:containerId`, or "new" reads as an id. */}
        <Route
          path="/archive/containers/new"
          element={
            <RequireRole role="administrator">
              <AppShellLayout>
                <NewArchiveContainerPage />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/archive/containers/:containerId"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ArchiveContainerDetailPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/archive/containers/:containerId/datasets"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ArchiveDatasetsPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* Approvals moved under Projects. Notifications already sent carry the
            old link, so it redirects rather than 404s. */}
        <Route path="/approvals" element={<Navigate to="/projects/approvals" replace />} />
        <Route
          path="/projects"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ProjectsHub tab="projects" />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        {/* Before `/projects/:projectId`, or "approvals" reads as a project id. */}
        <Route
          path="/projects/approvals"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ProjectsHub tab="approvals" />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/projects/new"
          element={
            <RequireAuth>
              <AppShellLayout>
                <NewProject />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ProjectDetail />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/projects/:projectId/pipelines/new"
          element={
            <RequireAuth>
              <AppShellLayout>
                <NewPipeline />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/projects/:projectId/pipelines/:pipelineId"
          element={
            <RequireAuth>
              <AppShellLayout>
                <PipelineCanvas />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* Create — a type picker, then one page per type (Translate, Q&A).
            Every role (ADR-0018); the backend decides which collections. */}
        <Route
          path="/solutions/create"
          element={
            <RequireAuth>
              <AppShellLayout>
                <CreatePicker />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/solutions/create/translate"
          element={
            <RequireAuth>
              <AppShellLayout>
                <TranslatePage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* Create → Q&A — answer a questionnaire from collections, every role (ADR-0018). */}
        <Route
          path="/solutions/create/qa"
          element={
            <RequireAuth>
              <AppShellLayout>
                <CreateQaPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* Review — the statement / data checker, every role (ADR-0018). */}
        <Route
          path="/solutions/review"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ReviewPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* Quality — the ALCOA+ document scorecard, every role (ADR-0018). */}
        <Route
          path="/solutions/quality"
          element={
            <RequireAuth>
              <AppShellLayout>
                <QualityPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* Clinical → eTMF intake — results of the pipelines published to it,
            every role (ADR-0032). */}
        <Route
          path="/solutions/clinical/etmf-intake"
          element={
            <RequireAuth>
              <AppShellLayout>
                <EtmfIntakePage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {/* Clinical → eTMF completeness — sites × TMF zones from a Veeva vault
            the user may use, every role (ADR-0035). */}
        <Route
          path="/solutions/clinical/etmf-completeness"
          element={
            <RequireAuth>
              <AppShellLayout>
                <EtmfCompletenessPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/solutions/clinical/assignments"
          element={
            <RequireAuth>
              <AppShellLayout>
                <AssignmentsPage />
              </AppShellLayout>
            </RequireAuth>
          }
        />

        {SOLUTION_PLACEHOLDER_ROUTES.map(({ path, slug }) => (
          <Route
            key={path}
            path={path}
            element={
              <RequireAuth>
                <AppShellLayout>
                  <SolutionPlaceholderPage slug={slug} />
                </AppShellLayout>
              </RequireAuth>
            }
          />
        ))}

        {/* Collections (the UI name for document indexes) and the Administration
            placeholder keep the gating of the section they sit in: Developer+,
            like Integrations and the audit log. The backend enforces ownership
            and grants regardless. */}
        <Route
          path="/development/collections"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <CollectionsList />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/development/collections/new"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <NewCollection />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/development/collections/:indexId"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <CollectionDetail />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/admin/documentation"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <SolutionPlaceholderPage slug="documentation" />
              </AppShellLayout>
            </RequireRole>
          }
        />

        {/* Explore — grounded chats over collections, every role (ADR-0018).
            The old /ai/chats routes redirect here. */}
        <Route
          path="/solutions/explore"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ExploreList />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/solutions/explore/:chatId"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ExploreChat />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        {/* Validation › 1st line support — Explore plus tickets (same components,
            `surface="support"`; only these chats can raise a ticket). */}
        <Route
          path="/solutions/validation/first-line-support"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ExploreList surface="support" />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/solutions/validation/first-line-support/:chatId"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ExploreChat surface="support" />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        {[
          "/solutions/validation/second-line-support",
          "/solutions/validation/second-line-support/:chatId",
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <RequireAuth>
                <AppShellLayout>
                  <SecondLineSupport />
                </AppShellLayout>
              </RequireAuth>
            }
          />
        ))}
        <Route path="/ai/chats" element={<Navigate to="/solutions/explore" replace />} />
        <Route path="/ai/chats/:chatId" element={<LegacyChatRedirect />} />
        {/* The assessment moved under Validation, where it is the journey's
          first stage. The old path keeps working — links to it are out there. */}
        <Route
          path="/ai/risk-assessment"
          element={<Navigate to="/validation/risk-assessment" replace />}
        />
        {/* Release validation (ADR-0019). Runs are Developer+; the risk
          assessment and "My tests" are every role — a tester is any Entra user.
          The first two are tabs of one hub, each keeping its own URL. */}
        {/* Release validation is the risk assessment only — no Runs tab. */}
        <Route
          path="/validation/runs"
          element={<Navigate to="/validation/risk-assessment" replace />}
        />
        <Route
          path="/validation/risk-assessment"
          element={
            <RequireAuth>
              <AppShellLayout>
                <ValidationHub tab="risk-assessment" />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        <Route
          path="/validation/runs/new"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <NewRun />
              </AppShellLayout>
            </RequireRole>
          }
        />
        <Route
          path="/validation/runs/:runId"
          element={
            <RequireRole role={["developer", "administrator"]}>
              <AppShellLayout>
                <RunDetail />
              </AppShellLayout>
            </RequireRole>
          }
        />
        {/* The Solutions-tree placeholder became the real page. */}
        <Route
          path="/solutions/validation/my-tests"
          element={<Navigate to="/validation/my-tests" replace />}
        />
        <Route
          path="/validation/my-tests"
          element={
            <RequireAuth>
              <AppShellLayout>
                <MyTests />
              </AppShellLayout>
            </RequireAuth>
          }
        />
        {/* The /modules/ai-agents and /modules/ai-tools placeholders are
          superseded by the real /ai/* routes above; the paths keep working for
          anyone holding a bookmark. */}
        <Route path="/modules/ai-agents" element={<Navigate to="/solutions/explore" replace />} />
        <Route
          path="/modules/ai-tools"
          element={<Navigate to="/validation/risk-assessment" replace />}
        />

        <Route path="*" element={<Navigate to="/me" replace />} />
      </Routes>
    </RolePreviewProvider>
  );
}
